import React from "react";
import {
  getDynamicInputTexts,
  getLabelTextsArray,
} from "../../../IpEMR/InspectionSummaries/constructTextFromValues";
import {
  cardiovascularOptions,
  neurologicalExaminationOptions,
  orthopedicOptions,
  respiratoryOptions,
  traumaOptions,
} from "../../../IpEMR/Options/clinicalImpressionOptions";
import { Typography } from "antd";
import "./ClinicalImpression.css";

const { Text } = Typography;

const toText = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  return null;
};

// Free text typed by the doctor is kept verbatim - only blank values are dropped,
// so the original line breaks and spacing survive into the summary.
const toEnteredText = (value) => {
  if (typeof value === "string") return value.trim() ? value : null;
  return toText(value);
};

const ClinicalImpression = ({ details }) => {
  // Each entry is a heading plus the bullet lines beneath it. Sections with no
  // entered values are dropped rather than rendered as an empty heading.
  const sections = [
    {
      key: "neurological",
      label: "Neurological System",
      items: [
        ...getLabelTextsArray(
          neurologicalExaminationOptions,
          details?.neurological_examination
        ),
        ...getDynamicInputTexts(details?.neurological_examination_others),
      ],
    },
    {
      key: "respiratory",
      label: "Respiratory",
      items: [
        ...getLabelTextsArray(
          respiratoryOptions,
          details?.respiratory_diagnosis
        ),
        ...getDynamicInputTexts(details?.respiratory_others),
      ],
    },
    {
      key: "cardiovascular",
      label: "Cardiovascular",
      items: [
        ...getLabelTextsArray(
          cardiovascularOptions,
          details?.cardiovascular_diagnosis
        ),
        ...getDynamicInputTexts(details?.cardiovascular_others),
      ],
    },
    {
      key: "orthopedic",
      label: "Orthopedic",
      items: getLabelTextsArray(
        orthopedicOptions,
        details?.orthopedic_diagnosis
      ),
    },
    {
      key: "trauma",
      label: "Trauma Diagnosis",
      items: [
        ...getLabelTextsArray(traumaOptions, details?.trauma_diagnosis),
        ...getDynamicInputTexts(details?.trauma_diagnosis_others),
      ],
    },
    {
      key: "clinical",
      label: "Clinical Diagnosis",
      preserveText: true,
      items: [toEnteredText(details?.clinical_diagnosis_notes)].filter(Boolean),
    },
    {
      key: "final",
      label: "Final Diagnosis",
      preserveText: true,
      items: [toEnteredText(details?.final_diagnosis_notes)].filter(Boolean),
    },
  ].filter((section) => section.items?.length);

  if (!sections.length) {
    return <Text type="secondary">No diagnosis recorded.</Text>;
  }

  return (
    <div className="clinical-impression-bullets">
      {sections.map((section) => (
        <div className="clinical-impression-section" key={section.key}>
          <Text className="clinical-impression-label">{section.label}</Text>
          {section.preserveText ? (
            section.items.map((item, index) => (
              <div
                className="clinical-impression-entered-text"
                key={`${section.key}-${index}`}
              >
                {item}
              </div>
            ))
          ) : (
            <ul>
              {section.items.map((item, index) => (
                <li key={`${section.key}-${index}`}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};

export default ClinicalImpression;
